<script setup lang="ts">
    import KButton from "@/components/Button.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import { Column, KTableAction, TableValue } from "@/components/KTable/types";
    import { useStore } from "@/store";
    import { ReceptforskrivningMall } from "@/store/modules/recept/receptforskrivningMallModule";
    import CellLakemedel from "@/tabs/Receptforskrivning/cells/CellLakemedel.vue";
    import ReceptforskrivningMallForm from "@/tabs/Receptforskrivning/Mall/Form/ReceptforskrivningMallForm.vue";
    import { getMalltypLabel } from "@/tabs/Receptforskrivning/Mall/helpers";
    import ReceptforskrivningMallListExpanded from "@/tabs/Receptforskrivning/Mall/List/ReceptforskrivningMallListExpanded.vue";
    import { openDialog } from "@/utils";
    import emitter from "tiny-emitter/instance";
    import { computed, onBeforeMount, onUnmounted, ref } from "vue";

    const store = useStore();

    const tableRef = ref();
    const selectedMall = ref<unknown | null>(null);
    const subsetName = ref<string>("default");
    const isLoading = ref<boolean>(true);
    const filterParams = ref<{
        typ?: ReceptforskrivningMall["typ"];
        q?: string;
    }>({
        typ: "ALLA",
    });
    const listLimit = ref<number>(
        JSON.parse(sessionStorage.getItem("receptforskrivningMall-table") ?? "{}")?.rows ?? 20
    );
    const listCount = ref<number>(0);
    const listPage = ref<number>(1);

    const columns = computed<Column[]>(() => [
        {
            field: "namn",
            header: "Namn",
            frozen: true,
            alignFrozen: "left",
        },
        {
            field: "malltyp",
            header: "Malltyp",
        },
        {
            field: "usedAt",
            header: "Senast använd",
            contentType: "datetime",
        },
        {
            field: "skapadAv",
            header: "Skapad av",
        },
        {
            field: "lakemedelInfo",
            header: "Läkemedel",
            bodyTemplate: CellLakemedel,
        },
        {
            field: "doseringsanvisning",
            header: "Doseringsanvisning",
            truncate: true,
            lines: 3,
        },
    ]);
    const currentProfileId = computed(() => store.getters["userData/currentProfileId"]);
    const mallar = computed<TableValue[]>(() => {
        return store.getters["receptforskrivningMall/mallarSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        }).map((mall: ReceptforskrivningMall) => ({
            id: mall.id,
            namn: mall.namn,
            malltyp: getMalltypLabel({ mall, currentProfileId: currentProfileId?.value }),
            usedAt: mall.used_at,
            skapadAv: mall.created_by_display.user_display.name,
            lakemedelInfo: store.getters["lakemedel/lakemedelById"]({
                id: mall.forskrivning.items[0]?.lakemedelId,
                displayMode: true,
            }),
            doseringsanvisning: mall.forskrivning.items[0]?.doseringsanvisning,
        }));
    });

    const hasFullAccess = computed(
        () => store.getters["userData/is_superuser"] || store.getters["userData/is_verksamhetsadmin"]
    );
    const profileId = computed(() => store.getters["userData/currentProfileId"]);

    const actions: KTableAction[] = [
        {
            title: "Redigera mall",
            show: (data) => {
                const mall: ReceptforskrivningMall = store.getters["receptforskrivningMall/mallById"]({ id: data.id });

                return hasFullAccess.value || mall.created_by === profileId.value;
            },
            callback: async ({ data }) => {
                store.dispatch("tabs/openTab", {
                    component: ReceptforskrivningMallForm,
                    props: {
                        mallId: data.id,
                    },
                });
            },
        },
        {
            title: "Ta bort mall",
            show: (data) => {
                const mall: ReceptforskrivningMall = store.getters["receptforskrivningMall/mallById"]({ id: data.id });

                return hasFullAccess.value || mall.created_by === profileId.value;
            },
            callback: async ({ data }) => {
                emitter.emit("open-dialog", {
                    type: "warning",
                    description: "Är du säker på att du vill ta bort mallen?",
                    buttons: [
                        {
                            title: "Nej",
                            type: "secondary",
                        },
                        {
                            title: "Ja, ta bort mallen",
                            type: "primary",
                            action: () => {
                                store.dispatch("receptforskrivningMall/removeMall", { id: data.id });
                            },
                        },
                    ],
                });
            },
        },
    ];

    const filterSchema = generateSchema({
        addToAll: {
            ignoreEmptyData: true,
        },
        schema: [
            {
                component: "KAutocomplete",
                name: "typ",
                label: "Malltyp",
                options: [
                    {
                        value: "ALLA",
                        label: "Alla mallar",
                    },
                    {
                        value: "PRIVAT",
                        label: "Mina mallar",
                    },
                    {
                        value: "GEMENSAM",
                        label: "Gemensamma mallar",
                    },
                    {
                        value: "ANDRAS",
                        label: "Andras mallar",
                    },
                ],
            },
        ],
    });

    const onRowClick = async ({ data }: { data: unknown }) => {
        selectedMall.value = data;
    };

    const onMenuClick = async ({ data }: { data: unknown }) => {
        selectedMall.value = data;
    };

    /**
     * Hämtar och lagrar läkemedel i store baserat på mallar som visas i listan och deras kopplade läkemedel
     * @param props Object
     * @param props.subsetName Namnet på subsetet av mallar som visas
     */
    const fetchLakemedel: (props: { subsetName: string }) => Promise<void> = async ({ subsetName }) => {
        const mallarSubset: ReceptforskrivningMall[] = store.getters["receptforskrivningMall/mallarSubset"]({
            subsetName,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        });

        // Plockar ut och filtrerar bort dubletter av id:n för läkemedel
        const lakemedelIds = new Set(
            mallarSubset.map((mall) => mall.forskrivning.items.map((forskrivning) => forskrivning.lakemedelId)).flat()
        );

        if (lakemedelIds.size === 0) {
            return;
        }

        await store.dispatch("lakemedel/fetchLakemedel", {
            filterParams: {
                // Sorterar för att undvika skapandet av fler subsets som egentligen har samma query params
                npl_pack_ids: [...lakemedelIds.values()].sort().join(","),
            },
        });
    };

    const fetchMallar = async ({
        page,
        forceRefresh,
        resetLocalSubsets,
        limit = listLimit.value,
    }: {
        page: number;
        limit?: number;
        forceRefresh?: boolean;
        resetLocalSubsets?: boolean;
    }) => {
        isLoading.value = true;

        try {
            const params = {
                ...(filterParams?.value ?? {}),
                ordering: "typ_sortering",
            };
            const {
                total,
                results,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("receptforskrivningMall/fetchMallar", {
                page,
                limit,
                forceRefresh,
                resetLocalSubsets,
                filterParams: params,
            });

            isLoading.value = false;
            subsetName.value = fetchedSubsetName;

            if (typeof total === "number") {
                listCount.value = total;
            }

            listPage.value = page;

            if (!forceRefresh && !results) {
                // Gör en hämtning i bakgrunden för att se till att data är up to date, tills vi har sockets
                const { total: backgroundTotal } = await store.dispatch("receptforskrivningMall/fetchMallar", {
                    page,
                    limit,
                    forceRefresh: true,
                    filterParams: params,
                });

                if (typeof total === "number" && listCount.value !== backgroundTotal) {
                    listCount.value = backgroundTotal;
                }
            }

            await fetchLakemedel({ subsetName: fetchedSubsetName });
        } catch (error) {
            isLoading.value = false;

            console.error(error);
            openDialog("Kunde inte hämta receptförskrivningsmallar", "error");
        }
    };

    const onFilter = ({ formData }: { formData: any }) => {
        filterParams.value = formData;
        fetchMallar({ page: 1 });
    };

    const getExpandedData = ({ data }: { data: TableValue }) => {
        const mall = store.getters["receptforskrivningMall/mallById"]({ id: data.id });

        return {
            ...data,
            ...(mall?.forskrivning?.items?.[0] ?? {}),
            linkFass: data.lakemedelInfo?.linkFass,
            substans: data.lakemedelInfo?.substans,
            atc: data.lakemedelInfo?.atc,
            forpackningsinnehall: data.lakemedelInfo?.forpackning,
        };
    };

    const openForm = () => {
        store.dispatch("tabs/openTab", {
            component: ReceptforskrivningMallForm,
        });
    };

    onBeforeMount(async () => {
        store.dispatch("receptforskrivningMall/addSubscriber");

        store.dispatch("nll/fetchBehandlingsorsaker");
        await fetchMallar({
            page: 1,
        });
    });

    onUnmounted(() => {
        store.dispatch("receptforskrivningMall/removeSubscriber");
    });
</script>

<template>
    <div class="receptforskrivning-mall-list">
        <KButton type="button" @click="openForm" width="content" size="secondary">
            <img src="@/assets/plus_circle_white.svg" alt="Skapa mall" />
            <span>Skapa mall</span>
        </KButton>
        <KFilter
            name="receptforskrivningMallList"
            tableStateName="receptforskrivningMall-table"
            :schema="filterSchema"
            :defaultValue="filterParams"
            :showTags="false"
            :expandable="false"
            searchParam="namn"
            @submit="onFilter"
        >
            <template #quick-actions="{ callback, isActive }">
                <KButton
                    :class="{
                        'is-active': isActive({
                            values: {
                                typ: 'ALLA',
                            },
                        }),
                    }"
                    type="button"
                    variant="selectable"
                    size="adaptable"
                    @click="
                        callback({
                            values: {
                                typ: 'ALLA',
                            },
                        })
                    "
                >
                    Alla mallar
                </KButton>
                <KButton
                    :class="{
                        'is-active': isActive({
                            values: {
                                typ: 'PRIVAT',
                            },
                        }),
                    }"
                    type="button"
                    variant="selectable"
                    size="adaptable"
                    @click="
                        callback({
                            values: {
                                typ: 'PRIVAT',
                            },
                        })
                    "
                >
                    Mina mallar
                </KButton>
                <KButton
                    :class="{
                        'is-active': isActive({
                            values: {
                                typ: 'GEMENSAM',
                            },
                        }),
                    }"
                    type="button"
                    variant="selectable"
                    size="adaptable"
                    @click="
                        callback({
                            values: {
                                typ: 'GEMENSAM',
                            },
                        })
                    "
                >
                    Gemensamma mallar
                </KButton>
                <KButton
                    v-if="hasFullAccess"
                    :class="{
                        'is-active': isActive({
                            values: {
                                typ: 'ANDRAS',
                            },
                        }),
                    }"
                    type="button"
                    variant="selectable"
                    size="adaptable"
                    @click="
                        callback({
                            values: {
                                typ: 'ANDRAS',
                            },
                        })
                    "
                >
                    Andras mallar
                </KButton>
            </template>
        </KFilter>
        <KTable
            ref="tableRef"
            paginator
            alwaysShowPaginator
            name="receptforskrivningMall-table"
            expandedRowVariant="full-framed"
            width="unlimited"
            loadingMessage="Hämtar mallar"
            theme="grey"
            :value="mallar"
            :columns="columns"
            :actions="actions"
            :expandableRows="true"
            :multipleExpandableRows="false"
            :expandOnRowClick="true"
            :loading="isLoading"
            :totalRecords="listCount"
            @rowClick="onRowClick"
            @menuClick="onMenuClick"
        >
            <template #expandedRow="{ data }">
                <ReceptforskrivningMallListExpanded :data="getExpandedData({ data })" />
            </template>
        </KTable>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .receptforskrivning-mall-list {
        display: flex;
        flex-direction: column;
        gap: $row-gap;

        // För att låta tabellen växa automatiskt utifrån tillgängligt utrymme
        ::v-deep .k-table {
            min-height: 30rem;
        }
    }
</style>
